import React from 'react';
import {
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { ListingForm as ListingFormType, Listing } from '@/types';
import { MultiPhotoUpload } from '@/components/common/MultiPhotoUpload';
import { ListingsService } from '@/utils/listings-service';

interface PhotosTabProps {
  formData: ListingFormType;
  errors: Record<string, string>;
  onFormDataChange: (updates: Partial<ListingFormType>) => void;
  onErrorsChange: (errors: Record<string, string>) => void;
  clearErrors: (fields: string[]) => void;
  listing?: Listing;
}

export const PhotosTab: React.FC<PhotosTabProps> = ({
  formData,
  onFormDataChange,
  clearErrors,
  listing,
}) => {
  const handlePhotoUpload = async (file: File): Promise<string> => {
    if (!listing?.listingId) {
      throw new Error('Please save the listing first before uploading photos');
    }
    return await ListingsService.uploadListingPhoto(listing.listingId, file);
  };

  const handlePhotosChange = async (newPhotos: string[]) => {
    const currentPhotos = formData.photos;
    const removedPhotos = currentPhotos.filter(photo => !newPhotos.includes(photo));
    
    // Delete removed photos from server
    if (removedPhotos.length > 0 && listing?.listingId) {
      try {
        await Promise.all(
          removedPhotos.map(photoUrl => 
            ListingsService.deleteListingPhoto(listing.listingId, photoUrl)
          )
        );
      } catch (error) {
        console.error('Failed to delete photos:', error);
        // You might want to show an error message to the user here
      }
    }
    
    // Update local state
    onFormDataChange({ photos: newPhotos });
    clearErrors(['photos']);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Photos
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Add high-quality photos to showcase your property. The first photo will be used as the main listing image.
      </Typography>

      <MultiPhotoUpload
        photos={formData.photos}
        onPhotosChange={handlePhotosChange}
        onUpload={handlePhotoUpload}
        disabled={!listing?.listingId}
      />
      
      {!listing?.listingId && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Please save your listing first before uploading photos.
        </Alert>
      )}

      {formData.photos.length === 0 && listing?.listingId && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Add at least one photo to make your listing more attractive to guests.
        </Alert>
      )}

      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Photo Tips:</strong>
          <br />
          • Use natural lighting when possible
          <br />
          • Show all main areas: living room, bedrooms, kitchen, bathroom
          <br />
          • Include exterior shots and neighborhood highlights
          <br />
          • Keep photos current and accurate
          <br />
          • The first photo is your main listing image - make it count!
        </Typography>
      </Box>
    </Box>
  );
};

export default PhotosTab;